// The reads and route flags the Mission Control screens share (lanes B2 and B3; any lane may import it). Three exports
// and one seam, kept small on purpose:
//
//   useSettledRead(key, fn, opts?)   useRead whose every fetch is registered with the capture settle signal, which
//                                    stays unsettled until the data arrives (a no-op outside a capture build)
//   queryOf(raw)                     the query flags of a hash route: "#requisitions/R-1040?action=post" → action=post
//   withQuery(path, flags, base?)    the route with its flags replaced; null removes a flag, base keeps the others
//   settleWith(settle, fn)           the pure wrapping useSettledRead applies (the node test's seam)
//
// The screens' own words and record keys stay in each lane's data.ts; only this plumbing is shared.

import type { UseQueryOptions } from "@tanstack/react-query";
import type { AppError } from "@/api/runtime";
import { useRead } from "@/api/useTool";
import { useCaptureSettle, type Settle } from "@/mc";

/** fn with each call's promise handed to the settle signal before it is returned. */
export function settleWith<T>(settle: Settle, fn: () => Promise<T>): () => Promise<T> {
  return () => {
    const work = fn();
    settle.pending(work);
    return work;
  };
}

/** useRead whose every fetch is registered with the capture settle signal which stays unsettled until it arrives. */
export function useSettledRead<T>(key: readonly unknown[], fn: () => Promise<T>, opts: Partial<UseQueryOptions<T, AppError>> = {}) {
  const settle = useCaptureSettle();
  return useRead<T>(key, settleWith(settle, fn), opts);
}

/** The query flags of a hash route (`#requisitions/R-1040?action=post`); none when it has no `?`. */
export function queryOf(raw: string): URLSearchParams {
  const at = raw.indexOf("?");
  return new URLSearchParams(at < 0 ? "" : raw.slice(at + 1));
}

/** The route with its flags replaced (null removes a flag; base's other flags are kept, base itself is not changed). */
export function withQuery(path: string, flags: Readonly<Record<string, string | null>>, base?: URLSearchParams): string {
  const query = new URLSearchParams(base);
  for (const [key, value] of Object.entries(flags)) {
    if (value === null) query.delete(key);
    else query.set(key, value);
  }
  const text = query.toString();
  return text ? `${path}?${text}` : path;
}
